import * as React from 'react';
import { Modal, ModalBody, ModalFooter, Form, CustomDatePicker, ModalSize, CustomButton, ButtonType, CustomSubmitButton, NumberInput, TextInput, HiddenInput, AlertMessage, AlertType, Select } from "soprasteria-ui-library";
import { useAuth } from '../../../App';
import { RiskPotReportTreatmentReportingApi } from '../../../api/RiskPotReportTreatmentReportingApi';
import { ToothCodeApi } from '../../../api/ToothCodeApi';
import { IRiskPotReportTreatment } from '../../../models/IRiskPotReport';
import { setMessage } from '../../../helpers/MessageHelper';
import { TreatmentsDropdown } from '../TreatmentReport/TreatmentsDropdown';
import { IPatientTreatment } from '../../../models/IPatientTreatment';
import { dateTimeToDateString, formatDate } from '../../../helpers/DateHelper';
import { ToothCodeDropdown } from '../../ToothCodeDropdown/ToothCodeDropdown';
import { IToothCode } from '../../../models/IToothCode';
import { IListItem } from 'soprasteria-ui-library/dist/models/IFormInput';
import { CarePerformerApi } from '../../../api/CarePerformerApi';


interface IRiskPotReportModal {
    riskPotReportId: number;
    onSuccess: (successMessage: string) => void;
    riskPotReportStartDate: Date;
    riskPotReportEndDate: Date;
    showTreatmentFormForm: boolean;
    onCloseForm: () => void; 
}

export const RiskPotReportModal: React.FunctionComponent<IRiskPotReportModal> = ({ riskPotReportId, onSuccess, riskPotReportStartDate, showTreatmentFormForm, onCloseForm, riskPotReportEndDate }) => {
    const auth = useAuth();
    const api = new RiskPotReportTreatmentReportingApi(auth);
    const toothCodeApi = new ToothCodeApi(auth);
    const carePerformerApi = new CarePerformerApi(auth);
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>();
    const [treatments, setTreatments] = React.useState<IPatientTreatment[]>();
    const [toothCodes, setToothCodes] = React.useState<IToothCode[]>();
    const [carePerformers, setCarePerformers] = React.useState<IListItem[]>([]);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [date, setDate] = React.useState<Date>(new Date());
    const [noCarePerformersMessage, setNoCarePerformersMessage] = React.useState<string>();
    const [carePerformerIsSelected, setCarePerformerIsSelected] = React.useState<boolean>(false);
    
    React.useEffect(() => {
        loadCarePerformerDentists();
        getToothCodes();
    }, [date]);

    const loadCarePerformerDentists = () => {
        setIsLoading(true);
        carePerformerApi.getCarePerformerWithDate(formatDate(date), (carePerformersData) => {
            
            let mappedCarePerformers = carePerformersData.map<IListItem>((carePerformer) => { return { text: carePerformer.carePerformerName, value: carePerformer.carePerformerId.toString() } });
            setCarePerformers(mappedCarePerformers);
            if (mappedCarePerformers.length === 0) {
                const newMessage = setMessage("Det finns ingen registrerad behandlare för mottagningen på angivet behandlingsdatum. Ansök om ny behandlare, alternativt om behandlaren är registrerad på annan mottagning, lägg till behandlaren på mottagningen.", noCarePerformersMessage ?? "");
                setNoCarePerformersMessage(newMessage);
            } else {
                setNoCarePerformersMessage("");
            }
        }, undefined, () => setIsLoading(false));
    }

    
    const getTreatments = (treatmentDate: Date, carePerfomerId: number) => {
        api.getTreatments(treatmentDate, carePerfomerId, (newTreatments: IPatientTreatment[]) => {
            const convertedTreatments = newTreatments.map<IPatientTreatment>((treatment) => { return { code: treatment.code, description: treatment.description, treatmentCodeId: treatment.treatmentCodeId } });          
            setTreatments(convertedTreatments)

        }, (error) => {
            const message = setMessage(error, errorMessage ?? "");
            setErrorMessage(message);
        });
    }

    const onTreatmentDateChange = (treatmentDate: Date | null) => {
        setCarePerformers([]);
        if (treatmentDate === null) {
            setDate(new Date());
        } else {
            setDate(treatmentDate);
        }
        loadCarePerformerDentists();
    }

    const getToothCodes = () => {
        toothCodeApi.getToothCodes((newToothCodes: IToothCode[]) => {
            const convertedToothCodes = newToothCodes.map<IToothCode>((toothCode) => { return { toothCodeId: toothCode.toothCodeId, code: toothCode.code } });
            setToothCodes(convertedToothCodes);
        }, (error) => {
            const message = setMessage(error, errorMessage ?? "");
            setErrorMessage(message);
        });
    }

    const onSubmit = (treatment: IRiskPotReportTreatment) => {
        setIsSubmitting(true);
        api.saveTreatment(treatment, () => {
            document.getElementById("close-add-risk-pot-report-treatment-modal")?.click();
            onSuccess("Åtgärdskoden har lagts till");
        }, (error: string) => {
            const message = setMessage(error, errorMessage ?? "");
            setErrorMessage(message);
        }, () => setIsSubmitting(false));
    }

    const resetFormState = () => {
        setTreatments([]);
        setIsSubmitting(false);
        setErrorMessage(undefined);
        onCloseForm();
    }

    const onCarePerformerChange = (carePerformerId: string | null) => {        
        if (carePerformerId !== null) {
            carePerformerApi.getCarePerformerWithId(parseInt(carePerformerId), (carePerformer) => {
                setCarePerformerIsSelected(true);
                setTreatments([]);
                getTreatments(date, carePerformer.carePerformerId);
            });          
        }      
    }

    const getMaxDateForTreatment = (): Date => {
        const currentDate = new Date();
        const convertedEndDate = new Date(dateTimeToDateString(riskPotReportEndDate));

        return currentDate > convertedEndDate ? convertedEndDate : currentDate
    }

    return (
        <Modal id="add-risk-pot-report-treatment-modal" modalSize={ModalSize.large} header="Lägg till åtgärdskod">
            {
                showTreatmentFormForm ?
                    <Form onSubmit={onSubmit}>
                        <ModalBody>
                            <div className="row">
                                <div className="col-12">
                                    <AlertMessage alertType={AlertType.danger} duration={3000}>{errorMessage}</AlertMessage>
                                    <AlertMessage alertType={AlertType.danger} duration={10000}>{noCarePerformersMessage}</AlertMessage>
                                    <HiddenInput name="riskPotReportId" value={riskPotReportId.toString()} />
                                    <CustomDatePicker
                                        name="treatmentDate"
                                        label="Behandlingsdatum"  
                                        tooltipDescription="Avser datumet när behandlingsåtgärden utfördes på patienten, ska överensstämma med journalen" 
                                        onChange={onTreatmentDateChange} 
                                        min={new Date(dateTimeToDateString(riskPotReportStartDate))}
                                        max={getMaxDateForTreatment()}
                                        required={new Date() > new Date(riskPotReportEndDate)}
                                        inlineLabel />
                                    <Select name="carePerformerId"
                                        label="Behandlare"
                                        placeholder="Välj en behandlare"
                                        options={carePerformers}
                                        onChange={onCarePerformerChange}
                                        isLoading={isLoading}
                                        inlineLabel
                                        required />

                                    <TreatmentsDropdown label="Åtgärdskod" name="treatmentCodeId" treatments={treatments ?? []} isLoading={carePerformerIsSelected && treatments === undefined} required placeholder="Välj åtgärdskod"/>
                                    <ToothCodeDropdown placeholder="Välj tand/käke" label="Tand/käke" name="toothCodeId" toothCodes={toothCodes ?? []} isLoading={toothCodes === undefined} isClearable />
                                    <TextInput label="Tandyta" name="toothSurface" inlineLabel />
                                    <NumberInput label="Antal" name="quantity" minValue={1} defaultValue={"1"} disabled inlineLabel required />
                                </div>
                            </div>
                        </ModalBody>
                        <ModalFooter>
                            <CustomButton buttonType={ButtonType.general} buttonText="Avbryt" data-dismiss="modal" id="close-add-risk-pot-report-treatment-modal" onClick={() => resetFormState()} />
                            <CustomSubmitButton isLoading={isSubmitting}>Lägg till</CustomSubmitButton>
                        </ModalFooter>
                    </Form>
                    : null
            }
        </Modal>

    )
}